// src/stage2/sceneExterior/ChainGate.jsx
import React, { useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

const CHAIN_GATE_URL = '/stageAssets/stage2/scene/Stage2Exterior-ChainGate.glb';

export function ChainGate({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
}) {
  const { scene } = useGLTF(CHAIN_GATE_URL);
  const { gl } = useThree();

  const gate = useMemo(() => {
    if (!scene) return null;

    const clone = scene.clone(true);
    const maxAniso = gl.capabilities.getMaxAnisotropy();

    const prepTexture = (tex) => {
      if (!tex) return;
      tex.anisotropy = maxAniso;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.needsUpdate = true;
    };

    clone.traverse((obj) => {
      if (!obj.isMesh || !obj.material) return;

      // IMPORTANT: clone materials so edits don’t affect other instances
      const materials = Array.isArray(obj.material)
        ? obj.material.map((m) => m.clone())
        : [obj.material.clone()];

      materials.forEach((m) => {
        // Your original requirement
        m.side = THREE.DoubleSide;

        // Texture anti-shimmer
        prepTexture(m.map);
        prepTexture(m.alphaMap);

        // If this material uses alpha (typical for chainlink cutouts), kill the crawl
        const usesAlpha =
          m.transparent === true ||
          !!m.alphaMap ||
          (m.map && m.map.format === THREE.RGBAFormat) ||
          (typeof m.opacity === 'number' && m.opacity < 1);

        if (usesAlpha) {
          m.transparent = true;

          // Key shimmer killer for chainlink-style cutouts:
          // tune 0.35–0.7 if you need (higher = “more solid” edges)
          m.alphaTest = Math.max(m.alphaTest || 0, 0.5);

          // Transparency depth artifacts reducer
          m.depthWrite = false;

          // Helps thin alpha details when MSAA is enabled
          m.alphaToCoverage = true;
        }

        m.needsUpdate = true;
      });

      obj.material = Array.isArray(obj.material) ? materials : materials[0];
    });

    return clone;
  }, [scene, gl]);

  if (!gate) return null;

  return (
    <primitive
      object={gate}
      position={position}
      rotation={rotation}
      scale={scale}
      castShadow
      receiveShadow
    />
  );
}

useGLTF.preload(CHAIN_GATE_URL);
